// =====================================================================
//  ADMIN CONSOLE, COMMAND PALETTE (Ctrl/⌘ K)
// ---------------------------------------------------------------------
//  Keyboard-first navigation and actions. An administrator working a
//  queue shouldn't have to move their hand to the mouse to get from a
//  leave request to that person's record.
//
//  It offers three kinds of result:
//    · Jump to, every screen in the console.
//    · Actions, global commands (new employee, run payroll, export).
//    · People, live server-side search over the directory, so typing
//                a name is enough to open that person's record.
//
//  Actions are dispatched as a window event rather than passed down as
//  callbacks. That keeps the palette from having to know which page owns
//  which dialog: it announces the intent, navigates to the right screen,
//  and whichever page is mounted there picks it up.
// =====================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  CornerDownLeft,
  Download,
  FilePlus2,
  UserPlus,
  Wallet,
} from 'lucide-react';

import backendApi from '../api/backendApi';
import { countFor, navGroupsForRole } from './nav';
import { Avatar, useDebounced, useEscape, useScrollLock, cx } from './ui';

/** Fired at `window` so the mounted page can react to a palette action. */
export const ADMIN_ACTION_EVENT = 'hrms-admin-action';

export const dispatchAdminAction = (type, payload = {}) => {
  window.dispatchEvent(new CustomEvent(ADMIN_ACTION_EVENT, { detail: { type, ...payload } }));
};

/**
 * Subscribes a page to a palette action. Pages call this to open their own
 * dialogs, e.g. `useAdminAction('people:new', () => setFormOpen(true))`.
 */
export function useAdminAction(type, handler) {
  useEffect(() => {
    const onAction = (event) => {
      if (event.detail?.type === type) handler(event.detail);
    };
    window.addEventListener(ADMIN_ACTION_EVENT, onAction);
    return () => window.removeEventListener(ADMIN_ACTION_EVENT, onAction);
  }, [type, handler]);
}

const ACTIONS = [
  { key: 'people:new', label: 'Add an employee', icon: UserPlus, route: 'people', roles: ['admin', 'hr'] },
  { key: 'payroll:run', label: 'Run payroll', icon: Wallet, route: 'payroll', roles: ['admin', 'hr'] },
  { key: 'people:export', label: 'Export employee directory', icon: Download, route: 'people' },
  { key: 'reports:open', label: 'Build a report', icon: FilePlus2, route: 'reports' },
];

export default function CommandPalette({ open, onClose, onNavigate, counts, role = 'admin' }) {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounced = useDebounced(query.trim(), 220);

  useEscape(onClose, open);
  useScrollLock(open);

  // Reset on each open so the palette never reopens mid-search.
  useEffect(() => {
    if (open) {
      setQuery('');
      setCursor(0);
      // Focus after paint, otherwise the animation steals it.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  /* ---- people search (server-side, only once it's worth a request) --- */
  const { data: people = [] } = useQuery({
    queryKey: ['admin', 'palette-people', debounced],
    queryFn: async () => {
      const { rows } = await backendApi.entities.Employee.page({
        search: debounced,
        limit: 6,
        sort: 'full_name',
        order: 'asc',
      });
      return rows;
    },
    enabled: open && debounced.length >= 2,
    staleTime: 30_000,
  });

  /* ---- build the flat, navigable result list ------------------------ */
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (text) => !q || String(text).toLowerCase().includes(q);
    const out = [];

    navGroupsForRole(role).forEach((group) => {
      group.items.forEach((item) => {
        if (match(item.label) || match(group.label)) {
          out.push({
            kind: 'nav',
            id: `nav:${item.key}`,
            label: item.label,
            section: 'Jump to',
            meta: group.label,
            icon: item.icon,
            badge: countFor(item, counts),
            run: () => onNavigate(item.key),
          });
        }
      });
    });

    ACTIONS.filter((action) => !action.roles || action.roles.includes(role))
      .filter((action) => match(action.label))
      .forEach((action) => {
        out.push({
          kind: 'action',
          id: `action:${action.key}`,
          label: action.label,
          section: 'Actions',
          icon: action.icon,
          run: () => {
            onNavigate(action.route);
            // Let the destination page mount before it can hear the intent.
            setTimeout(() => dispatchAdminAction(action.key), 60);
          },
        });
      });

    people.forEach((person) => {
      out.push({
        kind: 'person',
        id: `person:${person.id}`,
        label: person.full_name || person.email,
        section: 'People',
        meta: person.position || person.department || person.role,
        person,
        run: () => {
          onNavigate('people');
          setTimeout(() => dispatchAdminAction('people:open', { id: person.id }), 60);
        },
      });
    });

    return out;
  }, [query, role, counts, people, onNavigate]);

  // Keep the cursor inside the (shrinking) result list as the user types.
  useEffect(() => {
    setCursor((prev) => (prev >= results.length ? 0 : prev));
  }, [results.length]);

  const runAt = (index) => {
    const item = results[index];
    if (!item) return;
    onClose();
    item.run();
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((prev) => (results.length ? (prev + 1) % results.length : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((prev) => (results.length ? (prev - 1 + results.length) % results.length : 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      runAt(cursor);
    }
  };

  // Keep the highlighted row visible while arrowing through a long list.
  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  if (!open) return null;

  let lastSection = null;

  return (
    <>
      <div className="ac-overlay" style={{ zIndex: 70 }} onClick={onClose} aria-hidden="true" />
      <div className="ac-cmdk-scroll" onClick={onClose}>
        <div
          className="ac-cmdk"
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
          onClick={(event) => event.stopPropagation()}
        >
          <input
            ref={inputRef}
            className="ac-cmdk-input"
            placeholder="Search screens, actions and people…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            aria-label="Command palette search"
          />

          <div className="ac-cmdk-list" ref={listRef}>
            {results.length === 0 ? (
              <div style={{ padding: '26px 14px', textAlign: 'center', fontSize: 12.5, color: 'var(--ac-faint)' }}>
                No matches for “{query}”
              </div>
            ) : (
              results.map((item, index) => {
                const showSection = item.section !== lastSection;
                lastSection = item.section;
                const active = index === cursor;
                return (
                  <React.Fragment key={item.id}>
                    {showSection ? <div className="ac-cmdk-section">{item.section}</div> : null}
                    <button
                      type="button"
                      data-active={active}
                      className={cx('ac-cmdk-item', active && 'ac-cmdk-item--active')}
                      onMouseEnter={() => setCursor(index)}
                      onClick={() => runAt(index)}
                    >
                      {item.kind === 'person' ? (
                        <Avatar name={item.label} email={item.person?.email} size="sm" />
                      ) : item.icon ? (
                        <item.icon size={15} strokeWidth={1.85} style={{ flex: '0 0 15px' }} />
                      ) : null}

                      <span className="ac-cmdk-item-text">{item.label}</span>

                      {item.badge > 0 ? <span className="ac-nav-count">{item.badge}</span> : null}
                      {item.meta ? <span className="ac-cmdk-item-meta">{item.meta}</span> : null}
                      {active ? (
                        <CornerDownLeft size={12} strokeWidth={2} style={{ color: 'currentColor', opacity: 0.6 }} />
                      ) : (
                        <ArrowRight size={12} strokeWidth={2} style={{ opacity: 0.18 }} />
                      )}
                    </button>
                  </React.Fragment>
                );
              })
            )}
          </div>

          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              padding: '8px 12px',
              borderTop: '1px solid var(--ac-line-soft)',
              background: 'var(--ac-sunken)',
              fontSize: 10.5,
              color: 'var(--ac-faint)',
            }}
          >
            <span>
              <span className="ac-kbd">↑↓</span> navigate
            </span>
            <span>
              <span className="ac-kbd">Enter</span> open
            </span>
            <span>
              <span className="ac-kbd">Esc</span> close
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
